/* eslint-disable max-lines-per-function */
import { ReactComponent as EmptyAttachIcon } from 'img/icons/emptyAttach.svg';
import { ReactComponent as EmptyBinIcon } from 'img/icons/emptyBin.svg';
import { ReactComponent as LoopIcon } from 'img/icons/loop.svg';
import { ReactComponent as EmptyFolderIcon } from 'img/icons/uploadStroke.svg';
import React, { memo, ReactElement, useCallback, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { publishHelper } from 'reactApp/appHelpers/publishHelper';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { setButtonTypeClickAction } from 'reactApp/modules/upload/upload.module';
import { ButtonLink } from 'reactApp/ui/ButtonLink/ButtonLink';
import { CopyWeblink } from 'reactApp/ui/CopyWeblink/CopyWeblink';
import { FileIcon } from 'reactApp/ui/FileIcon/FileIcon';
import { LinkIcon } from 'reactApp/ui/VKUIIcons';
import { sendGa } from 'reactApp/utils/ga';

import styles from './Empty.css';

interface HomeProps {
    type: 'folder';
    storage: EStorageType;
    canUpload?: boolean;
    canCoshare?: boolean;
    setInputs?: (input: HTMLInputElement) => void;
    weblink?: string;
    id?: string;
}

type Props =
    | HomeProps
    | {
          type: 'search';
          searchText?: string;
          searchType?: string;
          storage: EStorageType;
          id?: string;
      }
    | {
          type: 'gallery';
          searchType?: string;
          storage: EStorageType;
          id?: string;
          canUpload?: boolean;
      };

/* TODO: удалить эту и другие референсы к поиску после успешного эксперимента "new_search_UI" */
const mapTypeName = {
    image: 'Изображения',
    audio: 'Музыка',
    video: 'Видео',
    document: 'Документы',
    pdf: 'Файлы PDF',
    spreadsheet: 'Таблицы',
    presentation: 'Презентации',
    archive: 'Архивы',
    folder: 'Папки',
};

export const Empty = memo((props: Props): ReactElement => {
    const { storage = EStorageType.search } = props;
    const { canCoshare, weblink, id = '' } = props as HomeProps;
    const inputRef = useRef<HTMLInputElement>(null);
    const dispatch = useDispatch();

    const onUploadClick = useCallback(() => {
        if (inputRef.current) {
            inputRef.current.click();
        }
    }, [inputRef.current]);
    const handlePublishing = useCallback(() => {
        sendGa('empty-foldr', 'click', 'publish');
        publishHelper({ item: { id } });
    }, []);
    const handleCopy = useCallback(() => {}, []);

    useEffect(() => {
        if (inputRef.current && 'setInputs' in props) {
            props.setInputs?.(inputRef.current);
        }
    }, ['setInputs' in props && props.setInputs, 'canUpload' in props && props.canUpload]);

    useEffect(() => {
        sendGa('empty-foldr', 'show');
        dispatch(setButtonTypeClickAction('click'));
    }, [id]);

    const getIcon = () => {
        if (props.type === 'search' || storage === EStorageType.feed || storage === EStorageType.quotaCleaner) {
            return <LoopIcon />;
        }

        const { canUpload } = props;

        if (storage === EStorageType.trashbin) {
            return <EmptyBinIcon />;
        } else if (storage === EStorageType.attaches) {
            return <EmptyAttachIcon />;
        } else if (storage === EStorageType.gallery) {
            return null;
        } else if (canUpload) {
            return <EmptyFolderIcon />;
        }
    };

    const getTitle = () => {
        if (props.type === 'search') {
            const { searchText, searchType } = props;

            const type = mapTypeName[searchType || ''];

            if (searchText && type) {
                return (
                    <span>
                        По запросу <span className={styles.highlight}>{searchText}</span> {type.toLowerCase()} не&nbsp;
                        {type === mapTypeName.audio ? 'найдена' : 'найдены'}
                    </span>
                );
            } else if (type) {
                return (
                    <span>
                        {type} не&nbsp;{type === mapTypeName.audio ? 'найдена' : 'найдены'}
                    </span>
                );
            }
            return (
                <span>
                    По запросу <span className={styles.highlight}>{searchText}</span> ничего не&nbsp;найдено
                </span>
            );
        } else if (props.type === 'gallery') {
            const { searchType } = props;

            return searchType === 'mobile' ? 'Сейчас у вас нет мобильных фотографий' : 'Сейчас у вас нет фотографий';
        }

        const { storage } = props;

        if (storage === EStorageType.trashbin) {
            return 'Корзина пуста';
        } else if (storage === EStorageType.attaches) {
            return 'Сейчас у вас нет файлов из почты';
        } else if (storage === EStorageType.feed) {
            return 'По запросу ничего не найдено';
        } else if (storage === EStorageType.sharedIncoming) {
            return (
                <>
                    С вами пока ничем не поделились
                    <div className={styles.text}>здесь появятся файлы и папки, которыми с вами поделятся</div>
                </>
            );
        }
        return 'Папка пока пуста';
    };

    const getText = () => {
        if (props.type === 'search') {
            const { searchText, searchType } = props;
            const type = mapTypeName[searchType || ''];

            if (type && searchText) {
                return 'Проверьте правильность написания или выберите другой тип файлов';
            } else if (type) {
                return 'Попробуйте использовать поиск по названию  или выберите другой тип файлов';
            }
            return 'Проверьте правильность написания или используйте другой запрос';
        }

        const { storage, canUpload } = props;

        if (storage === EStorageType.trashbin) {
            return 'Вы всегда можете удалить файлы из корзины чтобы освободить место';
        } else if (storage === EStorageType.attaches) {
            return 'Все файлы из ваших почтовых переписок будут автоматически попадать сюда';
        } else if (canUpload) {
            return (
                <>
                    Перенесите файлы сюда или нажмите
                    <br />
                    на кнопку{' '}
                    <ButtonLink onClick={onUploadClick} fontInherit primary>
                        Загрузить
                    </ButtonLink>
                    <input type="file" multiple className={styles.input} ref={inputRef} />
                </>
            );
        }
    };

    return (
        <div className={styles.root}>
            <div className={styles.file}>
                <FileIcon type={storage === EStorageType.gallery ? 'image' : null} />
                <div className={styles.icon}>{getIcon()}</div>
            </div>
            <div className={styles.title}>{getTitle()}</div>
            <div className={styles.text}>{getText()}</div>
            {storage === EStorageType.home && canCoshare && (
                <div className={styles.controls}>
                    {weblink ? (
                        <CopyWeblink publicUrl={weblink} onCopy={handleCopy} onClick={handlePublishing} theme="folder" />
                    ) : (
                        <>
                            <div className={styles.link}>
                                <LinkIcon />
                            </div>
                            <div className={styles.publish} onClick={handlePublishing}>
                                Поделиться папкой по ссылке
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    );
});

Empty.displayName = 'Empty';
